/*
 * @Author: zwh2 zwh2@meitu.com
 * @Date: 2025-06-14 15:17:35
 * @LastEditors: zwh2 zwh2@meitu.com
 * @LastEditTime: 2025-10-16 13:38:56
 * @FilePath: /roboneo-web/src/common/utils/subscribeUtil.ts
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
import type {
  SubscribeEnv, SubscribePopupOptions, MeiDou, ClientInfo,
} from '@meitu/subscribe';
import { v4 as uuid } from 'uuid';
import { useOfficialStore } from '$/store/useOfficialStore';
import { useLanguageStore } from '$/store/languageStore';
import useThemeStore from '$/store/useThemeStore';
import { LanguageCode } from '$/store/languageStore/types';

export interface SubscribeInstanceOptions {
  oid?: string | number;
  groupId?: string;
}

interface SubscribeInstanceSlot {
  key?: string;
  instance?: MeiDou;
  pending?: Promise<MeiDou | undefined>;
  pendingKey?: string;
}

// 参考命名实例模式，只维护个人版和当前团队版两个实例槽位。
const personalSubscribeSlot: SubscribeInstanceSlot = {};
const teamSubscribeSlot: SubscribeInstanceSlot = {};

let subscribeGeneration = 0;

// 暂时用不上 先保留 可能需要调整
export const getBaseSubscribeConfig = (): SubscribePopupOptions['config'] => {
  const { token } = useOfficialStore.getState().userInfo;
  const { theme } = useThemeStore.getState();
  return {
    env: 'Release' as SubscribeEnv,
    accessToken: token,
    clientInfo: {
      appId: '',
      vipGroup: '',
      theme,
      categoryGroupCode: '',
    },
  };
};

// 暂时用不上 先保留 可能需要调整
export async function getSubscribePopup() {
  const { openSubscribePopup } = await import('@meitu/subscribe');
  const subscribeConfig = getBaseSubscribeConfig();
  return {
    subscribeConfig,
    openSubscribePopup,
  };
}

/**
 * 生成订阅 SDK 的账号隔离键，防止同一页面切换账号后复用旧鉴权实例。
 */
function getSubscribeAccountKey() {
  const { uid, token } = useOfficialStore.getState().userInfo;
  return `${uid || ''}:${token || ''}`;
}

/** 将订阅作用域中的 OID 规范化为 SDK 接受的字符串。 */
function normalizeSubscribeOid(oid?: string | number) {
  if (oid === undefined || oid === null || oid === '') return undefined;
  return String(oid);
}

/** 校验并规范化团队实例所需的完整组织信息。 */
function normalizeSubscribeTeam(options: SubscribeInstanceOptions = {}) {
  const oid = normalizeSubscribeOid(options.oid);
  const groupId = options.groupId || undefined;
  if (Boolean(oid) !== Boolean(groupId)) {
    throw new Error('团队订阅实例必须同时传入 oid 和 groupId');
  }
  return oid && groupId ? { oid, groupId } : undefined;
}

/** 生成包含登录态与团队作用域的实例键，配置变化时重建对应实例。 */
function getSubscribeInstanceKey(options: SubscribeInstanceOptions = {}) {
  const accountKey = getSubscribeAccountKey();
  const team = normalizeSubscribeTeam(options);
  return `${accountKey}:${team ? `team:${team.oid}:${team.groupId}` : 'personal'}`;
}

/** 获取个人版或当前团队版的命名实例槽位。 */
function getSubscribeInstanceSlot(options: SubscribeInstanceOptions = {}) {
  return normalizeSubscribeTeam(options) ? teamSubscribeSlot : personalSubscribeSlot;
}

/**
 * 清空当前订阅 SDK 上下文，登录态变化时必须在下次支付前重建。
 */
export function resetSubscribe() {
  subscribeGeneration += 1;
  personalSubscribeSlot.key = undefined;
  personalSubscribeSlot.instance = undefined;
  personalSubscribeSlot.pending = undefined;
  personalSubscribeSlot.pendingKey = undefined;
  teamSubscribeSlot.key = undefined;
  teamSubscribeSlot.instance = undefined;
  teamSubscribeSlot.pending = undefined;
  teamSubscribeSlot.pendingKey = undefined;
}

/** 清除当前团队订阅实例，下一次团队支付时按新组织信息懒初始化。 */
export function resetTeamSubscribe() {
  teamSubscribeSlot.key = undefined;
  teamSubscribeSlot.instance = undefined;
  teamSubscribeSlot.pending = undefined;
  teamSubscribeSlot.pendingKey = undefined;
}

/**
 * 使用调用开始时的账号快照初始化订阅 SDK，账号中途变更时丢弃过期实例。
 */
export const initSubscribe = async (options: SubscribeInstanceOptions = {}): Promise<MeiDou | undefined> => {
  const accountKey = getSubscribeAccountKey();
  const generation = subscribeGeneration;
  const { token } = useOfficialStore.getState().userInfo;
  const { currentCountryCode, env } = useOfficialStore.getState();
  const { languageCode } = useLanguageStore.getState().languageStore;
  const { MeiDou } = await import('@meitu/subscribe');
  const envMap = {
    release: 'Release',
    beta: 'Beta',
    pre: 'Pre',
    dev: 'Pre',
    development: 'Pre',
  };

  const { appId } = useOfficialStore.getState();
  const team = normalizeSubscribeTeam(options);
  // 订阅 SDK 使用 ko 表示韩语，这里只转换传给订阅 SDK 的参数，不反写全局语言状态。
  let language: LanguageCode | 'ko' = languageCode;
  language = language === LanguageCode.kor ? 'ko' : language;
  const newSubscribeInstance = new MeiDou({
    accessToken: token,
    env: envMap[env] as SubscribeEnv,
    clientInfo: {
      appId,
      countryCode: currentCountryCode,
      ...(team ? {
        oid: team.oid,
        groupId: team.groupId,
      } : {}),
      language: language as ClientInfo['language'],
    },
    requestOptions: {
      headers: {
        'Client-Business-Trace-Id': uuid(),
      },
    },
  });

  if (getSubscribeAccountKey() !== accountKey || subscribeGeneration !== generation) {
    return undefined;
  }

  return newSubscribeInstance;
};

/**
 * 返回当前账号/作用域专属的订阅 SDK 实例，并合并同一槽位下的并发初始化。
 */
export const getSubscribe = async (options: SubscribeInstanceOptions = {}): Promise<MeiDou | undefined> => {
  try {
    const instanceKey = getSubscribeInstanceKey(options);
    const instanceSlot = getSubscribeInstanceSlot(options);

    if (instanceSlot.instance && instanceSlot.key === instanceKey) {
      return instanceSlot.instance;
    }

    if (!instanceSlot.pending || instanceSlot.pendingKey !== instanceKey) {
      instanceSlot.pendingKey = instanceKey;
      instanceSlot.pending = initSubscribe(options);
    }

    const initPromise = instanceSlot.pending;
    const instance = await initPromise;
    if (instanceSlot.pending === initPromise) {
      instanceSlot.pending = undefined;
      instanceSlot.pendingKey = undefined;
    }

    // 初始化期间切换了账号或作用域时，立即重试，不向上层暴露旧实例。
    if (!instance || getSubscribeInstanceKey(options) !== instanceKey) {
      instanceSlot.instance = undefined;
      return await getSubscribe(options);
    }

    instanceSlot.key = instanceKey;
    instanceSlot.instance = instance;
    return instance;
  } catch (err) {
    console.warn('initSubscribe----error', err);
    return undefined;
  }
};
